import { useState, useMemo } from "react";
import { Check, ExternalLink, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { EntryMethodIcon } from "./EntryMethodIcon";

interface EntryButtonProps {
  label: string;
  description?: string | null;
  entriesAwarded: number;
  actionUrl?: string | null;
  iconType?: string | null;
  iconKey?: string | null;
  iconUrl?: string | null;
  isCompleted?: boolean;
  onSubmit: () => Promise<{ ok: boolean; awardedEntries: number; error?: string } | undefined>;
  disabled?: boolean;
  themeColors?: {
    buttonBackground?: string;
    buttonText?: string;
  };
}

// Helper to darken a hex color for hover states
function darkenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, (num >> 16) - amt);
  const G = Math.max(0, ((num >> 8) & 0x00ff) - amt);
  const B = Math.max(0, (num & 0x0000ff) - amt);
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}

export function EntryButton({
  label,
  description,
  entriesAwarded,
  actionUrl,
  iconType,
  iconKey,
  iconUrl,
  isCompleted,
  onSubmit,
  disabled,
  themeColors,
}: EntryButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);
  const [lastAwarded, setLastAwarded] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = async () => {
    if (isLoading || disabled) return;

    // Open action URL in new tab first
    if (actionUrl) {
      window.open(actionUrl, "_blank", "noopener,noreferrer");
    }

    setIsLoading(true);
    setJustCompleted(false);

    try {
      const result = await onSubmit();
      if (result && result.ok && !result.error) {
        setJustCompleted(true);
        setLastAwarded(result.awardedEntries);
        // Reset animation state after a delay
        setTimeout(() => setJustCompleted(false), 2000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const showSuccess = justCompleted || isCompleted;

  // Compute inline styles for theme colors
  const buttonStyles = useMemo(() => {
    if (showSuccess) {
      // Success state uses default green gradient
      return undefined;
    }

    if (themeColors?.buttonBackground && themeColors?.buttonText) {
      const bgColor = themeColors.buttonBackground;
      const hoverBg = darkenColor(bgColor, 15);

      return {
        backgroundColor: isHovered ? hoverBg : bgColor,
        color: themeColors.buttonText,
        borderColor: bgColor,
      };
    }

    return undefined;
  }, [themeColors, showSuccess, isHovered]);

  // Determine classes - use gradient classes only when no custom theme
  const hasCustomTheme = !showSuccess && themeColors?.buttonBackground && themeColors?.buttonText;

  return (
    <div
      className={cn(
        "relative w-full transition-all duration-300",
        justCompleted && "celebrate"
      )}
    >
      <button
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        disabled={isLoading || disabled || isCompleted}
        style={buttonStyles}
        className={cn(
          "w-full flex items-center justify-between h-auto py-4 px-6 group",
          "rounded-lg font-semibold transition-all duration-200",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:pointer-events-none disabled:opacity-50",
          "hover:scale-[1.02] active:scale-[0.98]",
          showSuccess && "bg-gradient-success text-success-foreground shadow-lg",
          !showSuccess && !hasCustomTheme && "bg-gradient-accent text-accent-foreground shadow-lg hover:shadow-accent-glow",
          hasCustomTheme && "shadow-lg hover:shadow-xl",
          !isLoading && !showSuccess && !disabled && "entry-button-pulse"
        )}
      >
        <div className="flex items-center gap-3">
          <div className={cn(
            "flex items-center justify-center w-10 h-10 rounded-lg transition-colors",
            showSuccess ? "bg-white/20" : "bg-white/10 group-hover:bg-white/20"
          )}>
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : showSuccess ? (
              <Check className="w-5 h-5" />
            ) : (
              <EntryMethodIcon
                iconType={iconType}
                iconKey={iconKey}
                iconUrl={iconUrl}
                className="text-current"
              />
            )}
          </div>

          <div className="text-left">
            <span className="text-base font-bold block">
              {showSuccess ? (isCompleted && !justCompleted ? "Completed" : `+${lastAwarded || entriesAwarded} Entries!`) : label}
            </span>
            {!showSuccess && (
              <span className="text-xs opacity-80">
                {description || `Earn ${entriesAwarded} ${entriesAwarded === 1 ? "entry" : "entries"}`}
              </span>
            )}
          </div>
        </div>

        {actionUrl && !showSuccess && (
          <ExternalLink className="w-4 h-4 opacity-60" />
        )}
      </button>
    </div>
  );
}
